import {createSelector} from 'reselect'
import {
    filterIdsBySearch,
    getCustomerMaEntities,
    getCustomerWaEntities,
    getProjectMaEntities,
    getProjectWaEntities,
    getSortedCustomerIdsByName,
    getSortedProjectIdsByName,
    getSortedTaskIdsByName,
    getTaskMaEntities, getTaskWaEntities
} from './utils'
import {getCustomers, getProjects, getTasks, getUsers} from '../../../entities/selectors'
import {HierarchyLevels} from 'constants'
import {getViewerManagementCounters} from '../common/selectors'

export default function(getTabState) {
    const getUserId = state => getTabState(state).userId
    const getTab = state => getTabState(state).tab
    const getTaskIds = state => getTabState(state).taskIds
    const getProjectIds = state => getTabState(state).projectIds
    const getCustomerIds = state => getTabState(state).customerIds
    const getManagementTaskIds = state => getTabState(state).manageCptIds.taskIds
    const getManagementProjectIds = state => getTabState(state).manageCptIds.projectIds
    const getManagementExcludingSelfProjectIds = state => getTabState(state).manageCptIds.projectIdsExcludingSelf
    const getManagementCustomerIds = state => getTabState(state).manageCptIds.customerIds
    const getManagementExcludingSelfCustomerIds = state => getTabState(state).manageCptIds.customerIdsExcludingSelf
    const isCustomerTabLoaded = state => getTabState(state).isLoadedCustomerTab
    const isProjectTabLoaded = state => getTabState(state).isLoadedProjectTab
    const isTaskTabLoaded = state => getTabState(state).isLoadedTaskTab
    const getUsersCounters = state => getTabState(state).userCounters
    const getViewerCounters = state => getViewerManagementCounters(state)
    const getSearch = state => getTabState(state).searchString
    const isSearchOpen = state => getTabState(state).searchOpen
    const isLoading = state => getTabState(state).loading
    const isLoaded = state => getTabState(state).loaded
    const isShowArchivedEntities = state => getTabState(state).isShowArchivedEntities

    const getLoadedTabs = createSelector(
        isCustomerTabLoaded,
        isProjectTabLoaded,
        isTaskTabLoaded,
        (customer, project, task) => ({
            [HierarchyLevels.CUSTOMER]: customer,
            [HierarchyLevels.PROJECT]: project,
            [HierarchyLevels.TASK]: task,
        })
    )

    const getCounters = createSelector(
        getUsersCounters,
        getViewerCounters,
        (userCounters, viewerCounters) => ({
            customers: {
                curValue: userCounters.customers,
                maxValue: viewerCounters.customers,
            },
            projects: {
                curValue: userCounters.projects,
                maxValue: viewerCounters.projects,
            },
            tasks: {
                curValue: userCounters.tasks,
                maxValue: viewerCounters.tasks,
            },
        })
    )

    const getPanelUser = createSelector(
        getUserId,
        getUsers,
        (userId, users) => users[userId]
    )

    const getPanelUserFirstName = createSelector(
        getPanelUser,
        (user) => {
            return (user) ? user.firstName : ""
        }
    )

    const getPanelUserFML = createSelector(
        getPanelUser,
        (user) => {
            return (user) ? {
                firstName: user.firstName,
                middleInitial: user.middleInitial,
                lastName: user.lastName,
            } : {}
        }
    )

    const getFilteredTaskIds = createSelector(
        getTaskIds,
        getTasks,
        getSearch,
        (ids, tasks, search) => {
            return filterIdsBySearch(ids, tasks, search)
        }
    )

    const getFilteredProjectIds = createSelector(
        getProjectIds,
        getProjects,
        getSearch,
        (ids, projects, search) => {
            return filterIdsBySearch(ids, projects, search)
        }
    )

    const getFilteredCustomerIds = createSelector(
        getCustomerIds,
        getCustomers,
        getSearch,
        (ids, customers, search) => {
            return filterIdsBySearch(ids, customers, search)
        }
    )

    const getSortedTaskIds = createSelector(
        getFilteredTaskIds,
        getTasks,
        getProjects,
        getCustomers,
        getSortedTaskIdsByName,
    )

    const getSortedProjectIds = createSelector(
        getFilteredProjectIds,
        getProjects,
        getCustomers,
        getSortedProjectIdsByName,
    )

    const getSortedCustomerIds = createSelector(
        getFilteredCustomerIds,
        getCustomers,
        getSortedCustomerIdsByName,
    )

    const selectTaskMaEntities = createSelector(
        getSortedTaskIds,
        getTasks,
        getProjects,
        getCustomers,
        getManagementTaskIds,
        getManagementProjectIds,
        getManagementExcludingSelfProjectIds,
        getManagementCustomerIds,
        getManagementExcludingSelfCustomerIds,
        getTaskMaEntities,
    )

    const selectTaskWaEntities = createSelector(
        getSortedTaskIds,
        getTasks,
        getProjects,
        getCustomers,
        getManagementTaskIds,
        getManagementProjectIds,
        getManagementExcludingSelfProjectIds,
        getManagementCustomerIds,
        getManagementExcludingSelfCustomerIds,
        getTaskWaEntities,
    )

    const selectProjectWaEntities = createSelector(
        getSortedProjectIds,
        getProjects,
        getCustomers,
        getManagementProjectIds,
        getManagementExcludingSelfProjectIds,
        getManagementCustomerIds,
        getManagementExcludingSelfCustomerIds,
        getProjectWaEntities,
    )

    const selectProjectMaEntities = createSelector(
        getSortedProjectIds,
        getProjects,
        getCustomers,
        getManagementProjectIds,
        getManagementExcludingSelfProjectIds,
        getManagementCustomerIds,
        getManagementExcludingSelfCustomerIds,
        getProjectMaEntities,
    )

    const selectCustomerWaEntities = createSelector(
        getSortedCustomerIds,
        getCustomers,
        getManagementCustomerIds,
        getManagementExcludingSelfCustomerIds,
        getCustomerWaEntities,
    )

    const selectCustomerMaEntities = createSelector(
        getSortedCustomerIds,
        getCustomers,
        getManagementCustomerIds,
        getManagementExcludingSelfCustomerIds,
        getCustomerMaEntities,
    )

    return {
        getUserId,
        getTab,
        getTaskIds,
        getProjectIds,
        getCustomerIds,
        getManagementTaskIds,
        getManagementProjectIds,
        getManagementExcludingSelfProjectIds,
        getManagementCustomerIds,
        getManagementExcludingSelfCustomerIds,
        isTaskTabLoaded,
        isProjectTabLoaded,
        isCustomerTabLoaded,
        getUsersCounters,
        getViewerCounters,
        getSearch,
        isSearchOpen,
        getLoadedTabs,
        getCounters,
        getPanelUser,
        getPanelUserFirstName,
        getPanelUserFML,
        getFilteredTaskIds,
        getFilteredProjectIds,
        getFilteredCustomerIds,
        getSortedTaskIds,
        getSortedProjectIds,
        getSortedCustomerIds,
        selectTaskWaEntities,
        selectTaskMaEntities,
        selectProjectWaEntities,
        selectProjectMaEntities,
        selectCustomerWaEntities,
        selectCustomerMaEntities,
        isLoaded,
        isLoading,
        isShowArchivedEntities,
    }
}



// WEBPACK FOOTER //
// ./web/js/administration/userManagement/hierarchyAssignmentsPanelTab/selectorsGenerator.js